// path: src/Collection.js
import React, { useMemo, useState } from "react";
import "./App.css";
import characters from "./Characters.json";

const normalizeId = (id) => {
    const n = Number(id);
    return Number.isFinite(n) ? n : id;
};

const getImgSrc = (card) => `imgs/${card.image}`;

const Collection = ({ unlockedIds = [], foilUnlockedIds = [], onBackHome }) => {
    const [previewCard, setPreviewCard] = useState(null);

    const unlockedSet = useMemo(() => {
        return new Set((unlockedIds || []).map(normalizeId));
    }, [unlockedIds]);

    const foilUnlockedSet = useMemo(() => {
        return new Set((foilUnlockedIds || []).map(normalizeId));
    }, [foilUnlockedIds]);

    const total = characters.length;

    const unlockedCount = characters.reduce((acc, c) => {
        const id = normalizeId(c.id);
        return acc + (unlockedSet.has(id) ? 1 : 0);
    }, 0);

    const foilUnlockedCount = characters.reduce((acc, c) => {
        const id = normalizeId(c.id);
        return acc + (foilUnlockedSet.has(id) ? 1 : 0);
    }, 0);

    const closePreview = () => {
        setPreviewCard(null);
    };

    const previewCardIsFoil =
        previewCard && foilUnlockedSet.has(normalizeId(previewCard.id));

    return (
        <div className="collectionScreen">
            <div className="collectionHeader">
                <div>
                    <h1 className="impactFont collectionTitle">Collection</h1>
                    <div className="collectionProgress">
                        Regular: {unlockedCount}/{total} | Foil: {foilUnlockedCount}/{total}
                    </div>
                </div>

                <button className="collectionBackButton" onClick={onBackHome}>
                    Home
                </button>
            </div>

            <div className="collectionGrid" role="list" aria-label="Card collection">
                {characters.map((c) => {
                    const id = normalizeId(c.id);
                    const unlocked = unlockedSet.has(id);
                    const foilUnlocked = foilUnlockedSet.has(id);

                    return (
                        <div className="collectionItem" role="listitem" key={c.id}>
                            <button
                                type="button"
                                className={`collectionImageButton ${
                                    unlocked ? "collectionImageButton--unlocked" : ""
                                }`}
                                onClick={() => {
                                    if (unlocked) {
                                        setPreviewCard(c);
                                    }
                                }}
                                disabled={!unlocked}
                                aria-label={
                                    unlocked
                                        ? `View larger image of ${c.name}`
                                        : `${c.name} locked`
                                }
                            >
                                <div className={foilUnlocked ? "collectionImageFoilWrap" : ""}>
                                    <img
                                        src={getImgSrc(c)}
                                        alt={
                                            foilUnlocked
                                                ? `${c.name} foil version unlocked`
                                                : unlocked
                                                ? `${c.name} unlocked`
                                                : `${c.name} locked`
                                        }
                                        className={`collectionImage ${
                                            unlocked
                                                ? "collectionImage--unlocked"
                                                : "collectionImage--locked"
                                        }`}
                                        loading="lazy"
                                    />
                                </div>
                            </button>

                            <div className="collectionName">
                                <span>{c.name}</span>
                                <span
                                    className={`collectionFoilIcon ${
                                        foilUnlocked
                                            ? "collectionFoilIcon--unlocked"
                                            : "collectionFoilIcon--locked"
                                    }`}
                                    title={foilUnlocked ? "Foil unlocked" : "Foil locked"}
                                    role="img"
                                    aria-label={foilUnlocked ? "Foil unlocked" : "Foil locked"}
                                >
                                    ✨
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="collectionHint">
                Cards are greyed out until obtained. Foil versions are rare and display
                with a rainbow glow border once found.
            </div>

            {previewCard && (
                <div
                    className="collectionPreviewOverlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${previewCard.name} enlarged card preview`}
                    onClick={closePreview}
                >
                    <div
                        className="collectionPreviewPanel"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {/* <button
                            type="button"
                            className="collectionPreviewClose"
                            onClick={closePreview}
                            aria-label="Close card preview"
                        >
                            ×
                        </button> */}

                        <div
                            className={
                                previewCardIsFoil
                                    ? "collectionPreviewFoilWrap collectionImageFoilWrap"
                                    : "collectionPreviewFoilWrap"
                            }
                        >
                            <img
                                src={getImgSrc(previewCard)}
                                alt={`${previewCard.name} enlarged`}
                                className="collectionPreviewImage"
                            />
                        </div>

                        <div className="collectionPreviewName">
                            {previewCard.name}
                            {previewCardIsFoil && (
                                <span
                                    className="collectionFoilIcon collectionFoilIcon--unlocked"
                                    title="Foil unlocked"
                                    role="img"
                                    aria-label="Foil unlocked"
                                >
                                    ✨
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Collection;